import { QuotaUsageType } from "@common-shared/config/oneVerify/oneVerifyCoverage.config"
import { PolicyModalityCode } from "@common-shared/config/oneVerify/oneVerifyPolicies.config"
import type {
    IOneVerifyDemoBlockedMutationRouteMessage,
    IOneVerifyDemoDiscoverPolicyDefinition,
    IOneVerifyDemoGeneralPolicyDefinition,
    IOneVerifyDemoMedicaidPolicyDefinition,
    IOneVerifyDemoPolicyDefinition,
    IOneVerifyDemoPolicyDisplay,
    IOneVerifyDemoSmartScanPolicyDefinition,
} from "@common-shared/types/oneVerifyDemo.types"

export enum OneVerifyDemoPolicyId {
    // Estimate (/estimate)
    generalDavidChen = "generalDavidChen",
    generalLiamMcAllister = "generalLiamMcAllister",
    generalSarahThompson = "generalSarahThompson",
    generalEthanWright = "generalEthanWright",
    generalRonnieWesley = "generalRonnieWesley",
    generalMemberNotFound = "generalMemberNotFound",

    // Discover / SmartScan
    discoverEthanWright = "discoverEthanWright",
    smartScanCharlotteReed = "smartScanCharlotteReed",

    // Medicaid
    medicaidMichaelFarmer = "medicaidMichaelFarmer",
    medicaidSuzanneBullard = "medicaidSuzanneBullard",
}

export const COMMON_ONE_VERIFY_DEMO_CONFIG = {
    jwtIssuer: "one-verify-api",
    jwtAudience: "one-verify-demo",

    demoAmplitudeUserPrefix: "demo",

    provisionedDemoEmails: ["demo@meetnirvana.com"],

    jwtExpirationSeconds: 12 * 60 * 60, // 12 hours

    // Non-GET routes allowed for demo sessions.
    // Paths use :practiceId placeholder (normalized at runtime via
    // OneVerifyRouteHelper). Enforced on frontend (HttpHelper) and backend
    // (OneVerifyCognitoAuthenticatedMiddleware).
    allowedMutationRoutes: [
        "/v1/one-verify/practices/:practiceId/coverage/general",
        "/v1/one-verify/practices/:practiceId/coverage/discover",
        "/v1/one-verify/practices/:practiceId/coverage/smart-scan",
        "/v1/one-verify/practices/:practiceId/coverage/medicaid",
        // Validate-only: demo users can review CSV upload results; create remains blocked.
        "/v1/one-verify/practices/:practiceId/network-status/rules/validate",
        "/v1/one-verify/practices/:practiceId/payer-mappings/bulk/validate",
    ],

    // Fallback toast when a blocked demo mutation doesn't match a route prefix below.
    defaultBlockedMutationMessage:
        "This action is disabled in the demo environment.",

    // Feature-specific toasts for blocked demo mutations.
    // First matching prefix wins; keep more specific prefixes above broader ones.
    blockedMutationRouteMessages: [
        {
            prefix: "/v2/one-verify/practices/:practiceId/history/export",
            message:
                "CSV export is available on a full OneVerify account. Contact us to unlock exports for your organization.",
        },
        {
            // Single + bulk recheck both hit .../history/:id/retry
            prefix: "/v2/one-verify/practices/:practiceId/history/",
            message:
                "Re-running checks uses live coverage data and counts against your quota. Sign up for a full account to run checks on your patients.",
        },
        {
            prefix: "/v1/one-verify/practices/:practiceId/overrides",
            message:
                "The Overrides report and coverage override tools are available on a full OneVerify account.",
        },
        {
            prefix: "/v1/one-verify/practices/:practiceId/comments",
            message:
                "Policy comments help teams collaborate on coverage follow-up. Available on a full OneVerify account.",
        },
        {
            prefix: "/v1/one-verify/practices/:practiceId/integrations",
            message:
                "Integrations connect to your EHR and billing systems. Sign up to configure integrations for your practice.",
        },
        {
            prefix: "/v1/one-verify/practices/:practiceId/users",
            message:
                "User management is available to practice administrators on a full account.",
        },
        {
            prefix: "/v1/one-verify/practices/:practiceId/invites",
            message:
                "User management is available to practice administrators on a full account.",
        },
        {
            prefix: "/v1/one-verify/practices/:practiceId/assignments",
            message:
                "Workflow assignment tools help teams route policies. Available on a full OneVerify account.",
        },
        {
            prefix: "/v1/one-verify/practices/:practiceId/network-status",
            message:
                "Saving network status rules is available on a full OneVerify account. You can still upload and review a CSV in demo mode.",
        },
        {
            prefix: "/v1/one-verify/practices/:practiceId/payer-mappings",
            message:
                "Saving payer mappings is available on a full OneVerify account. You can still upload and review a CSV in demo mode.",
        },
        {
            prefix: "/v1/one-verify/practices/:practiceId/configurations",
            message:
                "Changing payer mapping mode is available on a full OneVerify account.",
        },
        {
            prefix: "/v1/one-verify/practices/:practiceId/ehr-writes",
            message:
                "Writing coverage results back to your EHR requires a configured integration. Sign up for a full OneVerify account to enable EHR write-back.",
        },
        {
            prefix: "/v1/one-verify/practices/:practiceId/saved-filters",
            message:
                "Saved report filters help your team reuse views across Search and Reports. Available on a full OneVerify account.",
        },
        {
            prefix: "/v1/one-verify/practices/:practiceId/support",
            message:
                "Reporting a coverage issue sends details to our support team for your organization. Sign up for a full account to submit support requests.",
        },
        {
            prefix: "/v1/one-verify/practices/:practiceId/payers",
            message:
                "Managing practice payers and contracted rates is available on a full OneVerify account.",
        },
        {
            prefix: "/v1/one-verify/practices/:practiceId/biller-groups",
            message:
                "Biller groups organize users for workflow routing and assignments. Available on a full OneVerify account.",
        },
    ] satisfies IOneVerifyDemoBlockedMutationRouteMessage[],

    defaultModalityCode: PolicyModalityCode.PSY,

    defaultProviderNpi: "1234567890",

    // Session charge in cents.
    defaultSessionCharge: 20000, // $200

    defaultInNetwork: true,

    defaultDiscoverZip: "10001",

    buildGeneralPolicy(
        id: OneVerifyDemoPolicyId,
        firstName: string,
        lastName: string,
        payerExternalId: string,
        memberId: string,
        dob: string,
        display: IOneVerifyDemoPolicyDisplay,
        options?: {
            payerName?: string
            inNetwork?: boolean
            zipCode?: string
        },
    ): IOneVerifyDemoGeneralPolicyDefinition {
        return {
            id,
            label: display.label,
            shortDescription: display.shortDescription,
            longDescription: display.longDescription,
            checkType: QuotaUsageType.GENERAL,
            coverageRequest: {
                modalityCode: this.defaultModalityCode,
                providerNpi: this.defaultProviderNpi,
                sessionCharge: this.defaultSessionCharge,
                inNetwork: options?.inNetwork ?? this.defaultInNetwork,
                payerExternalId,
                payerName: options?.payerName || payerExternalId,
                firstName,
                lastName,
                memberId,
                dob,
                zipCode: options?.zipCode,
            },
        }
    },

    buildDiscoverPolicy(
        id: OneVerifyDemoPolicyId,
        firstName: string,
        lastName: string,
        payerExternalId: string,
        dob: string,
        zipCode: string,
        display: IOneVerifyDemoPolicyDisplay,
        options?: {
            payerName?: string
            inNetwork?: boolean
        },
    ): IOneVerifyDemoDiscoverPolicyDefinition {
        return {
            id,
            label: display.label,
            shortDescription: display.shortDescription,
            longDescription: display.longDescription,
            checkType: QuotaUsageType.DISCOVER,
            coverageRequest: {
                modalityCode: this.defaultModalityCode,
                providerNpi: this.defaultProviderNpi,
                sessionCharge: this.defaultSessionCharge,
                inNetwork: options?.inNetwork ?? this.defaultInNetwork,
                payerExternalId,
                payerName: options?.payerName || payerExternalId,
                firstName,
                lastName,
                dob,
                zipCode,
            },
        }
    },

    buildSmartScanPolicy(
        id: OneVerifyDemoPolicyId,
        firstName: string,
        lastName: string,
        dob: string,
        zipCode: string,
        display: IOneVerifyDemoPolicyDisplay,
        options?: {
            inNetwork?: boolean
        },
    ): IOneVerifyDemoSmartScanPolicyDefinition {
        return {
            id,
            label: display.label,
            shortDescription: display.shortDescription,
            longDescription: display.longDescription,
            checkType: QuotaUsageType.SMART_SCAN,
            coverageRequest: {
                modalityCode: this.defaultModalityCode,
                providerNpi: this.defaultProviderNpi,
                sessionCharge: this.defaultSessionCharge,
                inNetwork: options?.inNetwork ?? this.defaultInNetwork,
                firstName,
                lastName,
                dob,
                zipCode,
            },
        }
    },

    buildMedicaidPolicy(
        id: OneVerifyDemoPolicyId,
        firstName: string,
        lastName: string,
        state: string,
        dob: string,
        display: IOneVerifyDemoPolicyDisplay,
        memberId?: string,
    ): IOneVerifyDemoMedicaidPolicyDefinition {
        return {
            id,
            label: display.label,
            shortDescription: display.shortDescription,
            longDescription: display.longDescription,
            checkType: QuotaUsageType.MEDICAID,
            coverageRequest: {
                modalityCode: this.defaultModalityCode,
                providerNpi: this.defaultProviderNpi,
                firstName,
                lastName,
                state,
                dob,
                memberId,
            },
        }
    },

    _generalPolicies: undefined as
        | IOneVerifyDemoGeneralPolicyDefinition[]
        | undefined,
    get generalPolicies(): IOneVerifyDemoGeneralPolicyDefinition[] {
        if (!this._generalPolicies) {
            this._generalPolicies = [
                this.buildGeneralPolicy(
                    OneVerifyDemoPolicyId.generalDavidChen,
                    "David",
                    "Chen",
                    "NYBLS",
                    "YUN12031213",
                    "1978-05-01",
                    {
                        label: "Standard · Active · In-Network · David Chen",
                        shortDescription:
                            "Active in-network member with a complete set of benefits returned",
                        longDescription:
                            "A clean, successful eligibility check. David is active on a PPO and in-network, so the response comes back fully populated: $25 copay, $2,200 deductible ($1,750 remaining), and $5,000 out-of-pocket max ($4,550 remaining). The best first check to run.",
                    },
                ),
                this.buildGeneralPolicy(
                    OneVerifyDemoPolicyId.generalLiamMcAllister,
                    "Liam",
                    "McAllister",
                    "TXBLS",
                    "CSGA7654321",
                    "1980-04-03",
                    {
                        label: "Standard · Active · Out-of-Network · Liam McAllister",
                        shortDescription:
                            "Active member who is out-of-network - triggers the network alert",
                        longDescription:
                            "Liam's coverage is active and his benefits look billable, but he's out-of-network, so the network alert fires. Use this to show how Nirvana catches an out-of-network member before a claim goes out the door.",
                    },
                    { inNetwork: true },
                ),
                this.buildGeneralPolicy(
                    OneVerifyDemoPolicyId.generalSarahThompson,
                    "Sarah",
                    "Thompson",
                    "BC001",
                    "DUJ123456789",
                    "1985-03-14",
                    {
                        label: "Standard · Active · Full Benefits · Sarah Thompson",
                        shortDescription:
                            "Active member with every response field populated, including a secondary policy",
                        longDescription:
                            "The most detailed response in the demo set. Sarah's plan returns both a copay and coinsurance, deductible and out-of-pocket progress, $0 telehealth, and a flag for a second active policy. Use this to walk through every section of the results view.",
                    },
                ),
                this.buildGeneralPolicy(
                    OneVerifyDemoPolicyId.generalEthanWright,
                    "Ethan",
                    "Wright",
                    "87726",
                    "894726153",
                    "1990-04-27",
                    {
                        label: "Standard · Inactive Policy · Ethan Wright",
                        shortDescription:
                            "Member whose coverage has lapsed - returns inactive",
                        longDescription:
                            "Ethan's policy comes back inactive across coverage, plan, and mental health benefits. Run this first, then run the same member through SmartScan to show Nirvana finding his real, active coverage elsewhere.",
                    },
                    // Smart Scan recovery for this member uses Fairfax VA zip
                    { zipCode: "22031" },
                ),
                this.buildGeneralPolicy(
                    OneVerifyDemoPolicyId.generalRonnieWesley,
                    "Ronnie",
                    "Wesley",
                    "87726",
                    "00001MHUC",
                    "2007-02-21",
                    {
                        label: "Standard · Partial Benefits (206) · Ronnie Wesley",
                        shortDescription:
                            "Payer confirms coverage but can't provide the copay or coinsurance",
                        longDescription:
                            "A realistic partial-benefits response. The payer confirms Ronnie is active and returns his $5,820 deductible, but can't determine his cost-share, so the response comes back as a 206 with a clear explanation. Shows how Nirvana handles incomplete payer data transparently instead of guessing.",
                    },
                    { payerName: "87726 (demo payer)" },
                ),
                this.buildGeneralPolicy(
                    OneVerifyDemoPolicyId.generalMemberNotFound,
                    "Demo",
                    "Member",
                    "87726",
                    "00001MHMNF",
                    "1990-01-01",
                    {
                        label: "Standard · Member Not Found",
                        shortDescription:
                            'Payer doesn\'t recognize the member ID - returns a clear "not found"',
                        longDescription:
                            "Any name and date of birth works with this member ID. The payer doesn't recognize the member, and the response says exactly that, with guidance to confirm the details and resubmit - a clean, actionable error, not a timeout or payer failure",
                    },
                    { payerName: "87726 (demo payer)" },
                ),
            ]
        }

        return this._generalPolicies
    },

    _discoverPolicies: undefined as
        | IOneVerifyDemoDiscoverPolicyDefinition[]
        | undefined,
    get discoverPolicies(): IOneVerifyDemoDiscoverPolicyDefinition[] {
        if (!this._discoverPolicies) {
            this._discoverPolicies = [
                this.buildDiscoverPolicy(
                    OneVerifyDemoPolicyId.discoverEthanWright,
                    "Ethan",
                    "Wright",
                    "62308",
                    "1990-04-27",
                    "22031",
                    {
                        label: "Discover · Active · Ethan Wright",
                        shortDescription:
                            "Finds active coverage for the member whose eligibility check came back inactive",
                        longDescription:
                            "The strongest moment in the demo. Ethan came back inactive on a standard check, but SmartScan - using only his name, date of birth, and zip - finds his real, active Choice Plus PPO through his employer (Amazon.com, Inc.), complete with member ID and full demographics.",
                    },
                    { payerName: "Cigna" },
                ),
            ]
        }

        return this._discoverPolicies
    },

    _smartScanPolicies: undefined as
        | IOneVerifyDemoSmartScanPolicyDefinition[]
        | undefined,
    get smartScanPolicies(): IOneVerifyDemoSmartScanPolicyDefinition[] {
        if (!this._smartScanPolicies) {
            this._smartScanPolicies = [
                this.buildSmartScanPolicy(
                    OneVerifyDemoPolicyId.smartScanCharlotteReed,
                    "Charlotte",
                    "Reed",
                    "1996-09-05",
                    "94110",
                    {
                        label: "SmartScan · Member Not Found · Charlotte Reed",
                        shortDescription:
                            'A demographic search that finds no coverage - returns "not found"',
                        longDescription:
                            "A name + date of birth + zip search that comes back empty. For contrast, search Charlotte Williams with the same DOB and zip - she is found, showing that matching runs on real identity data, not name similarity.",
                    },
                ),
            ]
        }

        return this._smartScanPolicies
    },

    _medicaidPolicies: undefined as
        | IOneVerifyDemoMedicaidPolicyDefinition[]
        | undefined,
    get medicaidPolicies(): IOneVerifyDemoMedicaidPolicyDefinition[] {
        if (!this._medicaidPolicies) {
            this._medicaidPolicies = [
                this.buildMedicaidPolicy(
                    OneVerifyDemoPolicyId.medicaidMichaelFarmer,
                    "Michael",
                    "Farmer",
                    "FL",
                    "1993-01-29",
                    {
                        label: "Medicaid · Active · Michael Farmer",
                        shortDescription:
                            "Active Florida Medicaid with the member's managed care plan identified",
                        longDescription:
                            'Michael is actively enrolled in Florida Medicaid at $0 cost to the member, and the response names his managed care organization: Sunshine State Health Plan. MCO attribution is the differentiator - most eligibility tools stop at "active Medicaid."',
                    },
                    "01MCOACTIVE",
                ),
                this.buildMedicaidPolicy(
                    OneVerifyDemoPolicyId.medicaidSuzanneBullard,
                    "Suzanne",
                    "Bullard",
                    "NY",
                    "2001-11-13",
                    {
                        label: "Medicaid · Active · State Plan · Suzanne Bullard",
                        shortDescription:
                            "Active New York Medicaid directly through the state - no managed care plan",
                        longDescription:
                            "Suzanne is enrolled in New York Medicaid through the state plan with no MCO. Pair her with Michael Farmer to show the difference between managed-care and state-plan Medicaid responses.",
                    },
                    "01STATEACTIVE",
                ),
            ]
        }

        return this._medicaidPolicies
    },

    _policies: undefined as IOneVerifyDemoPolicyDefinition[] | undefined,
    get policies(): IOneVerifyDemoPolicyDefinition[] {
        if (!this._policies) {
            this._policies = [
                ...this.generalPolicies,
                ...this.discoverPolicies,
                ...this.smartScanPolicies,
                ...this.medicaidPolicies,
            ]
        }

        return this._policies
    },
}
